
import React, { useState, useEffect } from 'react';
import { Lead } from '../types';
import { generateOutreach } from '../services/geminiService';

interface SalesAgentProps {
  leads: Lead[];
  onAddNote: (leadId: string, text: string) => void;
}

const SalesAgent: React.FC<SalesAgentProps> = ({ leads, onAddNote }) => {
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [selectedVettingPoints, setSelectedVettingPoints] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [outreach, setOutreach] = useState<{ whatsapp: string, emailSubject: string, emailBody: string, socialDm: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'WHATSAPP' | 'EMAIL' | 'DM'>('WHATSAPP');

  const selectedLead = leads.find(l => l.id === selectedLeadId);

  // Reset vetting points when lead changes
  useEffect(() => {
    setSelectedVettingPoints(new Set());
    setOutreach(null);
  }, [selectedLeadId]);

  const handleTogglePoint = (point: string) => {
    setSelectedVettingPoints(prev => {
      const next = new Set(prev);
      if (next.has(point)) next.delete(point);
      else next.add(point);
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!selectedLead) return;
    setIsGenerating(true);
    try {
      const result = await generateOutreach(selectedLead, Array.from(selectedVettingPoints));
      setOutreach(result);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Payload Copied.');
  };

  return (
    <div className="max-w-6xl mx-auto animate-in fade-in duration-700">
      <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-2xl mb-8 border-t-8 border-t-black">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h2 className="text-3xl font-black text-black tracking-tighter uppercase italic">Strike Sequence</h2>
            <p className="text-gray-500 font-bold text-[10px] uppercase tracking-widest">Generating high-impact payloads for Reel Impact Forged.</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={selectedLeadId} onChange={(e) => setSelectedLeadId(e.target.value)}
              className="bg-white border border-gray-200 rounded px-4 py-2 text-xs font-black uppercase outline-none focus:border-red-600 min-w-[200px]">
              <option value="">Select Target...</option>
              {leads.filter(l => l.isArchived).map(l => (
                <option key={l.id} value={l.id}>{l.businessName}</option>
              ))}
            </select>
            <button onClick={handleGenerate} disabled={!selectedLeadId || isGenerating}
              className="bg-red-600 text-white px-6 py-2 rounded font-black text-[10px] uppercase tracking-widest hover:bg-red-700 disabled:opacity-30 transition-all">
              {isGenerating ? 'Forging...' : 'Forge Payload'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {selectedLead ? (
            <div className="bg-black text-white p-6 rounded-xl h-fit">
              <div className="text-[8px] font-black text-red-600 uppercase tracking-[0.2em] mb-4">Target Intel</div>
              <h4 className="font-black text-sm uppercase italic mb-1">{selectedLead.businessName}</h4>
              <p className="text-[9px] text-gray-500 mb-6">{selectedLead.industry} | {selectedLead.province}</p>
              
              <div className="space-y-4 border-t border-gray-800 pt-6">
                <div className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Strike Points (Select to include)</div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {selectedLead.vettingBreakdown?.map((b, idx) => (
                    <div 
                      key={idx}
                      onClick={() => handleTogglePoint(`${b.category}: ${b.reason}`)}
                      className={`p-3 rounded border text-left cursor-pointer transition-all ${
                        selectedVettingPoints.has(`${b.category}: ${b.reason}`)
                          ? 'bg-red-600 border-red-700 text-white' 
                          : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-red-600/50'
                      }`}
                    >
                      <div className="text-[8px] font-black uppercase mb-1">{b.category}</div>
                      <div className="text-[10px] font-medium leading-tight">{b.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="lg:col-span-1 bg-gray-50 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-200 min-h-[400px]">
              <p className="text-[10px] font-black uppercase text-gray-400 italic">Select a target to begin</p>
            </div>
          )}

          <div className="lg:col-span-3">
            {!outreach && !isGenerating ? (
              <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 h-full flex flex-col items-center justify-center p-12 text-center">
                <i className="fa-solid fa-fire-burner text-3xl text-gray-200 mb-4"></i>
                <h3 className="text-sm font-black uppercase text-gray-400 mb-2">Payload Not Forged</h3>
                <p className="text-[10px] text-gray-400 max-w-xs font-bold">Select Strike Points and click 'Forge Payload' to generate optimized outreach messages.</p>
              </div>
            ) : isGenerating ? (
               <div className="bg-gray-50 rounded-xl border border-gray-100 h-full flex flex-col items-center justify-center p-12 text-center">
                  <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-6"></div>
                  <h3 className="text-sm font-black uppercase text-black mb-2 animate-pulse">Heating the Furnace</h3>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Personalizing data points for maximum impact...</p>
               </div>
            ) : outreach && (
              <div className="space-y-6">
                <div className="flex gap-1 mb-6 border-b border-gray-100">
                  {(['WHATSAPP', 'EMAIL', 'DM'] as const).map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                      className={`px-6 py-3 text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-400'}`}>
                      {tab}
                    </button>
                  ))}
                </div>

                <div className="bg-gray-50 p-8 rounded-xl border border-gray-100 min-h-[300px] relative group">
                  <button 
                    onClick={() => copyToClipboard(activeTab === 'WHATSAPP' ? outreach.whatsapp : activeTab === 'EMAIL' ? outreach.emailBody : outreach.socialDm)}
                    className="absolute top-4 right-4 text-gray-300 hover:text-red-600 transition-colors"
                  >
                    <i className="fa-solid fa-copy text-sm"></i>
                  </button>
                  
                  {activeTab === 'WHATSAPP' && <p className="text-sm text-black font-medium leading-relaxed italic">"{outreach.whatsapp}"</p>}
                  {activeTab === 'EMAIL' && (
                    <div>
                      <div className="text-[10px] font-black text-gray-400 uppercase mb-4 pb-2 border-b border-gray-200">Subject: <span className="text-black">{outreach.emailSubject}</span></div>
                      <p className="text-sm text-black font-medium leading-relaxed italic whitespace-pre-wrap">{outreach.emailBody}</p>
                    </div>
                  )}
                  {activeTab === 'DM' && <p className="text-sm text-black font-medium leading-relaxed italic">"{outreach.socialDm}"</p>}
                  
                  <div className="mt-10 flex gap-3">
                    <button onClick={() => copyToClipboard(activeTab === 'WHATSAPP' ? outreach.whatsapp : activeTab === 'EMAIL' ? outreach.emailBody : outreach.socialDm)}
                      className="bg-black text-white px-6 py-2.5 rounded text-[10px] font-black uppercase tracking-widest hover:bg-gray-900 transition-all shadow-lg">
                      Copy Payload
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesAgent;
